import { Flag, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';

// Shared session moderation types and labels (SESSION_MODERATION_PLAN.md).
// Where each piece lives on the server:
//   - the scan: on the context record of the reply it scored
//   - the verdict: on the session_review agent run
//   - the state: on the conversation's moderation row

// What the policy did after a review: nothing, a 24-hour ban on the visitor
// id, or that plus the IP (a second banned visitor from the same address).
export type ModerationBan = 'none' | 'visitor' | 'visitor_and_ip';

export type ModerationState = 'flagged' | 'reviewed' | 'banned' | 'dismissed' | 'cleared';

export interface ModerationScan {
    score?: number | null;
    categories?: string[];
    reason?: string | null;
    flagged?: boolean;
    review_run_id?: string | null;
    at?: string | null;
}

// States the team should look at. Mirrors ATTENTION_STATES on the server.
const ATTENTION_STATES: ModerationState[] = ['flagged', 'reviewed', 'banned'];

export function needsAttention(state?: string | null): boolean {
    return !!state && (ATTENTION_STATES as string[]).includes(state);
}

const CATEGORY_LABELS: Record<string, string> = {
    infrastructure_probing: "Probing the chat's infrastructure",
    prompt_injection: 'Prompt injection',
    prompt_extraction: "Trying to extract the agent's instructions",
    security_research: 'Security research or bug bounty activity',
    spam: 'Spam',
    abuse: 'Abuse',
    none: 'Not categorized',
};

export function categoryLabel(category?: string | null): string {
    return (category && CATEGORY_LABELS[category]) || CATEGORY_LABELS.none;
}

export const VERDICT_LABELS: Record<string, string> = {
    bad_faith: 'Bad faith',
    good_faith: 'Good faith',
    unclear: 'Unclear',
};

export const BAN_LABELS: Record<ModerationBan, string> = {
    none: 'No action taken',
    visitor: 'Visitor banned for 24 hours',
    visitor_and_ip: 'Visitor and IP address banned for 24 hours',
};

// Why a bad-faith verdict did not ban.
const NOT_BANNED_LABELS: Record<string, string> = {
    moderation_off: 'Not banned: moderation was turned off',
    verified_identity: 'Not banned: this visitor is a verified user',
    rep_owns_conversation: 'Not banned: a teammate had taken over the chat',
    no_visitor_id: 'Not banned: the visitor could not be identified',
    verdict_unclear: 'No action: the review was not sure',
    verdict_good_faith: 'No action: the review found no issue',
};

export function outcomeLabel(ban?: string | null, notBannedReason?: string | null): string {
    if (ban === 'visitor' || ban === 'visitor_and_ip') return BAN_LABELS[ban];
    if (notBannedReason && notBannedReason.startsWith('error:')) return 'Not banned: the ban could not be applied';
    return (notBannedReason && NOT_BANNED_LABELS[notBannedReason]) || BAN_LABELS.none;
}

export const STATE_LABELS: Record<ModerationState, string> = {
    flagged: 'Flagged, review pending',
    reviewed: 'Reviewed',
    banned: 'Banned',
    dismissed: 'Reviewed, no issue found',
    cleared: 'Cleared',
};

export function scorePercent(score?: number | null): string | null {
    return typeof score === 'number' ? `${Math.round(score * 100)}%` : null;
}

/** The scan of one exchange, as shown in the Analyze drawer. */
export function ModerationScanCard({ scan }: { scan: ModerationScan }) {
    const flagged = !!scan.flagged;
    const categories = (scan.categories || []).filter((category) => category !== 'none');
    const percent = scorePercent(scan.score);
    const Icon = flagged ? Flag : ShieldCheck;

    return (
        <div
            className={cn(
                'mt-4 rounded-lg border px-4 py-3',
                flagged ? 'border-orange-500/60' : 'border-border',
            )}
        >
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                    <Icon
                        className={cn(
                            'h-4 w-4 flex-shrink-0',
                            flagged ? 'text-orange-600 dark:text-orange-400' : 'text-muted-foreground',
                        )}
                    />
                    <span className="text-[13px] font-semibold">Session moderation</span>
                </div>
                {percent && (
                    <span className="text-[12px] tabular-nums text-muted-foreground" title="How likely the visitor is acting in bad faith">
                        Score {percent}
                    </span>
                )}
            </div>
            <p className="mt-1.5 text-[13px] leading-5">
                {flagged
                    ? scan.review_run_id
                        ? 'This exchange was flagged and sent for review.'
                        : 'This exchange was flagged.'
                    : 'This exchange was checked and not flagged.'}
            </p>
            {categories.length > 0 && (
                <p className="mt-1 text-[12px] leading-5 text-muted-foreground">
                    {categories.map((category) => categoryLabel(category)).join(', ')}
                </p>
            )}
            {scan.reason && (
                <p className="mt-1 text-[12px] leading-5 text-muted-foreground">{scan.reason}</p>
            )}
        </div>
    );
}
